import Icon from "@/v2-ui/icon"
import Button from "@/v2-backoffice-client/components/button"
import type { LayerDriftFixResult } from "@/v2-backoffice-client/layer/drift/resolve/useLayerDriftFix"

export type LayerDriftFixNoticeProps = {
  result: LayerDriftFixResult
  onDismiss(): void
}

function pluralReferences(count: number): string {
  return count === 1 ? "1 reference" : `${count} references`
}

/**
 * Explains what the Drift tab just did to the document in view. An edit
 * that appears without a word is indistinguishable from one the operator
 * made themselves and forgot, so the strip names the change, and says
 * that it is unsaved.
 */
function LayerDriftFixNotice(props: LayerDriftFixNoticeProps) {
  const { result, onDismiss } = props

  const isNoop = result.occurrences === 0

  return (
    <p className="mt-2 flex shrink-0 items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-2.5 py-1.5 text-xs">
      <Icon src={isNoop ? "info" : "swap"} size={12} className="shrink-0" />
      {isNoop ? (
        <>
          Nothing left to change —{" "}
          <code className="font-mono">{result.token}</code> is no longer
          referenced in this document.
        </>
      ) : result.renamedTo ? (
        <>
          Renamed {pluralReferences(result.occurrences)} of{" "}
          <code className="font-mono">{result.token}</code> to{" "}
          <code className="font-mono">{result.renamedTo}</code>. Unsaved — check
          the preview, then save. Cmd+Z undoes it.
        </>
      ) : (
        <>
          {pluralReferences(result.occurrences)} to{" "}
          <code className="font-mono">{result.token}</code> in this document;
          the first is selected.
        </>
      )}
      <Button
        variant="ghost"
        size="xs"
        iconSrc="x"
        isSquare
        className="ml-auto shrink-0"
        title="Dismiss"
        data-feature-code="UI_BackofficeLayers_DriftFix_Dismiss"
        onClick={onDismiss}
      />
    </p>
  )
}

export default LayerDriftFixNotice
